import i18next from 'i18next';

import {useModal} from 'web-app/react/modal-context/modal-context';
import * as GlobalEvent from 'web-app/services/global-event';
import * as SentryService from 'web-app/services/sentry';
import {isNonEmpty, type NonEmptyArray} from 'web-app/util/non-empty-array';

import {PlanTemplatesModal} from './plan-templates-modal';
import type {HandleSimplifiedUSBillingPlanModalWithTemplates, CreateSubView} from '../types';
import type {SimplifiedUSBillingPlanModalOpen} from '../hooks/use-simplified-us-billing-plan-modal';
import {usePlanTemplates} from '../hooks/use-plan-templates';
import * as Tracking from '../tracking';

type Args = {
    selectedChildIds: NonEmptyArray<string>;
    simplifiedUSBillingPlanModalOpen: SimplifiedUSBillingPlanModalOpen | null;
    siteId: string;
};

export const usePlanTemplatesModal = ({selectedChildIds, simplifiedUSBillingPlanModalOpen, siteId}: Args) => {
    const {planTemplates, loading} = usePlanTemplates({siteId});

    const {show, hide} = useModal({
        component: PlanTemplatesModal,
        modalProps: {
            fullScreenOnMobile: true,
        },
        contentFullHeight: true,
    });

    const handleSimplifiedUSBillingPlanModalWithTemplates: HandleSimplifiedUSBillingPlanModalWithTemplates = (
        templates,
        selectedTemplateId,
        subView?: CreateSubView | undefined,
    ) => {
        if (!simplifiedUSBillingPlanModalOpen) {
            return;
        }
        if (selectedTemplateId) {
            Tracking.templatePicked();
        } else {
            Tracking.startFromScratch();
        }

        simplifiedUSBillingPlanModalOpen(selectedChildIds, {
            view: 'create',
            subView: subView ?? 'bulk',
            templates,
            selectedTemplateId,
            handleAddPlanTemplatesModalClose: hide,
        });
    };

    const handleErrorMessage = () => {
        GlobalEvent.updateError(i18next.t('somethingWentWrong'));
        SentryService.captureMessage(`Failed opening Chill Bill templates modal from Billing Hub`);
    };

    return {
        open: (subView?: CreateSubView | undefined) => {
            if (!isNonEmpty(selectedChildIds)) {
                handleErrorMessage();
            } else if (!isNonEmpty(planTemplates) && simplifiedUSBillingPlanModalOpen) {
                simplifiedUSBillingPlanModalOpen(selectedChildIds, {view: 'create', subView: subView ?? 'bulk'});
            } else {
                show({
                    onClose: hide,
                    handleSimplifiedUSBillingPlanModalWithTemplates,
                    templates: planTemplates,
                });
            }
        },
        isTemplatesLoading: loading,
    };
};
